'use client';

import { createShadow } from '@larvitu/shared/utils';
import { Box } from '@mui/material';
import { styled } from '@mui/material/styles';

import type { Theme } from '@mui/material/styles';
import type { Property } from 'csstype';

interface ShadowBoxStandard {
  transitionDuration?: Property.TransitionDuration;
  transitionTimingFunction?:
    | string[]
    | Property.TransitionTimingFunction
    | Property.TransitionTimingFunction[]
    | undefined;
  transitionDelay?: Property.TransitionDelay;
}

interface ShadowBoxCustomShadowProps extends ShadowBoxStandard {
  min: keyof Theme['customShadows'];
  max: keyof Theme['customShadows'];
  minColor?: undefined;
  maxColor?: undefined;
}

interface ShadowBoxColoredShadowsProps extends ShadowBoxStandard {
  min?: undefined;
  max?: undefined;
  minColor: string;
  maxColor: string;
}

export type ShadowBoxProps =
  | ShadowBoxCustomShadowProps
  | ShadowBoxColoredShadowsProps;

export const ShadowBox = styled( Box, {
  shouldForwardProp: (prop) =>
    prop !== 'zoomFactor'
    && prop !== 'transitionDuration'
    && prop !== 'min'
    && prop !== 'min'
    && prop !== 'minColor'
    && prop !== 'maxColor'
    && prop !== 'transitionTimingFunction'
    && prop !== 'transitionDelay',
} )<ShadowBoxProps>(
  ({
    theme,
    transitionDuration,
    transitionTimingFunction,
    transitionDelay,
    minColor = 'rgb(255,255,255)',
    maxColor = 'rgb(0,0,0)',
    min,
    max,
  }) => ({
    '&>div,&>a>div': {
      transitionProperty: 'box-shadow',
      ...(transitionDuration && {
        transitionDuration: `${transitionDuration}s`,
      }),
      transitionTimingFunction,
      ...(transitionDelay && { transitionDelay: `${transitionDelay}s` }),
      willChange: 'box-shadow',
      boxShadow: createShadow( minColor ),
      '&:hover': {
        boxShadow: createShadow( maxColor ),
      },
      ...(min
        && max && {
        boxShadow: theme.customShadows[min],
        '&:hover': {
          boxShadow: theme.customShadows[max],
        },
      }),
    },
  })
);
